/**
 * The preview pane beside the document table: the open document's preview,
 * details and version history, and what can be done with it — ask Amoreg,
 * review tracked edits, save a new version, download, star.
 *
 * The pane reads the version named in the URL (`?v=`) exactly, else the
 * latest. Opening a document records it in Recent.
 *
 * @module components/apps/imanage/IManagePreviewPane
 */
import type { IManageDocument, IManageEntry } from "@amoreg/contracts/imanage";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  CopyIcon,
  DownloadIcon,
  FilePenLineIcon,
  HistoryIcon,
  MoreHorizontalIcon,
  SparklesIcon,
  StarIcon,
  UploadIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { useStartChatWithText } from "../../../hooks/useStartChatWithText";
import { base64ToBytes } from "../../../state/imanage";
import { Button } from "../../ui/button";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../../ui/menu";
import { toastManager } from "../../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../ui/tooltip";
import { askAboutDocumentPrompt, mimeTypeFor } from "./imanageAsk";
import {
  downloadName,
  fileKind,
  formatBytes,
  formatDateTime,
  formatWhen,
  rowAuthor,
} from "./imanageFormat";
import { recordRecent, refFromEntry, refKey, toggledFavorite, useIManageList } from "./imanageLocalLists";
import { documentKey, libraryOfId } from "./imanagePaths";
import { useDocumentContent, useVersions } from "./imanageQueries";
import { IManageDocumentPreview, PreviewSkeleton } from "./IManageDocumentPreview";
import { IManageViewTabs } from "./IManageLayout";
import { FileGlyph, fileKindLabel, InlineError, MatterNumber } from "./IManageParts";
import { IManageSaveVersionDialog } from "./IManageSaveVersionDialog";

type PaneTab = "preview" | "details" | "versions";

export interface PreviewPlace {
  /** The workspace and folder the document is shown in, when known. */
  readonly workspace?: IManageEntry | null;
  readonly folder?: IManageEntry | null;
}

export function IManagePreviewPane({
  organizationId,
  orgSegment,
  library: fallbackLibrary,
  documentId,
  versionId,
  place,
  canUpload,
  canSaveVersion,
  canDownload,
  onClose,
  onSelectVersion,
  onReview,
  canReview,
  onSaved,
}: {
  readonly organizationId: string;
  readonly orgSegment: string;
  readonly library: string;
  readonly documentId: string;
  /** A specific version to show; null for the latest. */
  readonly versionId: string | null;
  readonly place: PreviewPlace;
  readonly canUpload: boolean;
  readonly canSaveVersion: boolean;
  readonly canDownload: boolean;
  readonly onClose: () => void;
  readonly onSelectVersion: (versionId: string | null) => void;
  readonly onReview: (document: IManageDocument) => void;
  /** Whether this document can be reviewed here; reviewing needs its workspace. Defaults to yes. */
  readonly canReview?: ((document: IManageDocument) => boolean) | undefined;
  readonly onSaved: () => void;
}) {
  const library = libraryOfId(documentId) ?? fallbackLibrary;
  const [tab, setTab] = useState<PaneTab>("preview");
  const [saving, setSaving] = useState(false);
  const content = useDocumentContent(organizationId, library, versionId ?? documentId, versionId !== null);
  const versions = useVersions(organizationId, library, documentId);
  const [favorites, setFavorites] = useIManageList(organizationId, "favorites");
  const startChat = useStartChatWithText(organizationId);

  const document = content.data;
  const reviewBlocked = Boolean(document && canReview && !canReview(document));
  const latest = versions.data?.[0] ?? null;
  const entry: IManageEntry | null = document ?? latest;
  const isLatest = !versionId || (latest !== null && document?.id === latest.id);
  const starred = entry ? favorites.some((x) => refKey(x) === documentKey(entry.id)) : false;

  // Opening a document is a visit: it joins Recent once its profile is known.
  const openedId = document?.id ?? null;
  useEffect(() => {
    if (!document) return;
    recordRecent(
      organizationId,
      refFromEntry(
        document,
        {
          library,
          folderId: place.folder?.id ?? null,
          workspaceId: place.workspace?.id ?? document.workspaceId ?? null,
        },
        Date.now(),
      ),
    );
    // Record each opened version once, not on every re-render.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [openedId]);

  // A star belongs to the document, so it records the latest version, not the one on screen.
  const toggleStar = () => {
    const starredEntry = latest ?? entry;
    if (!starredEntry) return;
    setFavorites((list) =>
      toggledFavorite(
        list,
        refFromEntry(starredEntry, { library, workspaceId: place.workspace?.id ?? null, folderId: place.folder?.id ?? null }, Date.now()),
      ),
    );
  };

  const file = () =>
    document
      ? new File([base64ToBytes(document.content).slice()], downloadName(document), {
          type: mimeTypeFor(document.extension),
        })
      : null;

  const download = () => {
    const blob = file();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = blob.name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const ask = () => {
    if (!entry || !startChat) return;
    const attachment = file();
    void startChat(
      askAboutDocumentPrompt(document ?? entry, {
        library,
        workspace: place.workspace ?? null,
        folder: place.folder ?? null,
        attached: attachment !== null,
      }),
      attachment ? [attachment] : [],
    );
  };

  const copy = (text: string, title: string) => {
    void navigator.clipboard?.writeText(text).catch(() => {});
    toastManager.add({ type: "success", title });
  };

  const isWord = entry ? fileKind(entry) === "word" && entry.extension === "docx" : false;
  const workspaceId = place.workspace?.id ?? entry?.workspaceId ?? null;
  const notInWorkspace = !place.workspace && workspaceId;

  return (
    <aside
      aria-label="Document preview"
      data-imanage-preview-pane=""
      className="flex h-full min-h-0 w-full flex-col bg-background"
    >
      <header className="flex shrink-0 flex-col gap-2.5 border-b border-border/60 px-4 pt-3 pb-3">
        <div className="flex items-start gap-2.5">
          {entry ? <FileGlyph entry={entry} className="mt-0.5" /> : null}
          <h2 className="line-clamp-2 min-w-0 flex-1 text-[14px] font-semibold leading-snug">
            {entry?.name ?? (content.error ? "Document unavailable" : "Loading…")}
          </h2>
          <div className="-mt-1 -mr-1.5 flex shrink-0 items-center">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={starred ? "Remove from favorites" : "Add to favorites"}
                    aria-pressed={starred}
                    disabled={!entry}
                    onClick={toggleStar}
                    className={cn(starred && "text-warning")}
                  />
                }
              >
                <StarIcon aria-hidden className={cn(starred && "fill-current")} />
              </TooltipTrigger>
              <TooltipPopup>{starred ? "Unstar" : "Star"}</TooltipPopup>
            </Tooltip>
            <Menu>
              <MenuTrigger
                render={<Button variant="ghost" size="icon-xs" aria-label="More actions" disabled={!entry} />}
              >
                <MoreHorizontalIcon aria-hidden />
              </MenuTrigger>
              <MenuPopup align="end" className="min-w-52">
                {notInWorkspace && entry ? (
                  <MenuItem
                    render={
                      <Link
                        to="/$org/apps/imanage/w/$workspaceId"
                        params={{ org: orgSegment, workspaceId: notInWorkspace }}
                        search={{ doc: entry.id }}
                      />
                    }
                  >
                    <ArrowUpRightIcon aria-hidden />
                    Open in workspace
                  </MenuItem>
                ) : null}
                <MenuItem onClick={() => copy(window.location.href, "Link copied")}>
                  <CopyIcon aria-hidden />
                  Copy link
                </MenuItem>
                <MenuItem onClick={() => entry && copy(entry.id, "iManage ID copied")}>
                  <CopyIcon aria-hidden />
                  Copy iManage ID
                </MenuItem>
                <MenuSeparator />
                <MenuItem disabled={!document} onClick={download}>
                  <DownloadIcon aria-hidden />
                  Download
                </MenuItem>
              </MenuPopup>
            </Menu>
            <Tooltip>
              <TooltipTrigger
                render={<Button variant="ghost" size="icon-xs" aria-label="Close preview" onClick={onClose} />}
              >
                <XIcon aria-hidden />
              </TooltipTrigger>
              <TooltipPopup>Close · Esc</TooltipPopup>
            </Tooltip>
          </div>
        </div>
        {entry ? (
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
            <span className="font-mono tabular-nums">
              {entry.documentNumber !== undefined ? `#${entry.documentNumber}` : entry.id}
              {entry.version !== null ? ` · v${entry.version}` : ""}
            </span>
            <span aria-hidden>·</span>
            <span>{fileKindLabel(entry)}</span>
            {entry.size !== undefined ? (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{formatBytes(entry.size)}</span>
              </>
            ) : null}
            {rowAuthor(entry) ? (
              <>
                <span aria-hidden>·</span>
                <span className="truncate">{rowAuthor(entry)}</span>
              </>
            ) : null}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-1.5">
          <Button size="xs" onClick={ask} disabled={!entry || !startChat} data-imanage-ask="">
            <SparklesIcon aria-hidden />
            Ask Amoreg
          </Button>
          {isWord ? (
            <Button
              size="xs"
              variant="outline"
              disabled={!document || !canDownload || reviewBlocked}
              title={reviewBlocked ? "Open this document from its workspace to review edits." : undefined}
              onClick={() => document && !reviewBlocked && onReview(document)}
            >
              <FilePenLineIcon aria-hidden />
              Review edits
            </Button>
          ) : null}
          {isWord && canUpload ? (
            <Button size="xs" variant="outline" disabled={!document} onClick={() => setSaving(true)}>
              <UploadIcon aria-hidden />
              {canSaveVersion ? "Save new version" : "Save a copy"}
            </Button>
          ) : null}
          <Button size="xs" variant="ghost" disabled={!document} onClick={download}>
            <DownloadIcon aria-hidden />
            Download
          </Button>
        </div>
      </header>
      {!isLatest && document ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-border/60 bg-warning/8 px-4 py-1.5 text-xs">
          <HistoryIcon aria-hidden className="size-3.5 text-warning" />
          <span className="min-w-0 flex-1">
            Version {document.version} of {latest?.version ?? "?"}, from {formatWhen(document.modified)}.
          </span>
          <Button variant="ghost" size="xs" onClick={() => onSelectVersion(null)}>
            View latest
          </Button>
        </div>
      ) : null}
      <div className="flex shrink-0 items-end border-b border-border/60 px-4">
        <IManageViewTabs<PaneTab>
          label="Document views"
          value={tab}
          onValueChange={setTab}
          tabs={[
            { value: "preview", label: "Preview" },
            { value: "details", label: "Details" },
            {
              value: "versions",
              label: "Versions",
              ...(versions.data ? { count: versions.data.length } : {}),
            },
          ]}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto" data-imanage-pane-body={tab}>
        {tab === "preview" ? (
          content.error && !document ? (
            <InlineError className="m-4" message={content.error} onRetry={content.refresh} />
          ) : !document ? (
            <PreviewSkeleton />
          ) : (
            <IManageDocumentPreview key={document.id} document={document} />
          )
        ) : tab === "details" ? (
          entry ? (
            <Details entry={entry} place={place} library={library} />
          ) : (
            <PreviewSkeleton />
          )
        ) : (
          <Versions
            versions={versions.data}
            error={versions.error}
            onRetry={versions.refresh}
            shownId={document?.id ?? null}
            onSelect={(version, index) => onSelectVersion(index === 0 ? null : version.id)}
          />
        )}
      </div>
      {saving && document ? (
        <IManageSaveVersionDialog
          organizationId={organizationId}
          library={library}
          document={document}
          folder={place.folder ?? null}
          canSaveVersion={canSaveVersion}
          onClose={() => setSaving(false)}
          onSaved={onSaved}
        />
      ) : null}
    </aside>
  );
}

function Details({
  entry,
  place,
  library,
}: {
  readonly entry: IManageEntry;
  readonly place: PreviewPlace;
  readonly library: string;
}) {
  const rows: Array<[string, ReactNode]> = [];
  const add = (label: string, value: ReactNode | undefined | null) => {
    if (value !== undefined && value !== null && value !== "") rows.push([label, value]);
  };
  add("Type", fileKindLabel(entry));
  if (entry.email) {
    add("From", entry.emailFrom);
    add("To", entry.emailTo);
    add("Sent", entry.sentAt ? formatDateTime(entry.sentAt) : null);
  }
  add("Author", entry.author);
  add("Document", entry.documentNumber !== undefined ? `#${entry.documentNumber}` : null);
  add("Version", entry.version);
  add("Modified", entry.modified ? formatDateTime(entry.modified) : null);
  add("Created", entry.created ? formatDateTime(entry.created) : null);
  add("Size", entry.size !== undefined ? formatBytes(entry.size) : null);
  if (place.workspace)
    add(
      "Workspace",
      <span className="flex items-center gap-1.5">
        <MatterNumber entry={place.workspace} />
        {place.workspace.name}
      </span>,
    );
  add("Folder", place.folder?.name);
  add("Library", library);
  add("iManage ID", <span className="font-mono text-xs">{entry.id}</span>);
  return (
    <dl className="grid grid-cols-[7.5rem_1fr] gap-x-3 gap-y-2.5 px-5 py-4 text-[13px]">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Versions({
  versions,
  error,
  onRetry,
  shownId,
  onSelect,
}: {
  readonly versions: readonly IManageEntry[] | null;
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly shownId: string | null;
  readonly onSelect: (version: IManageEntry, index: number) => void;
}) {
  if (error && !versions) return <InlineError className="m-4" message={error} onRetry={onRetry} />;
  if (!versions) return <PreviewSkeleton />;
  return (
    <ol className="flex flex-col py-1" aria-label="Version history">
      {versions.map((version, index) => {
        const shown = version.id === shownId;
        return (
          <li key={version.id}>
            <button
              type="button"
              aria-current={shown ? "true" : undefined}
              onClick={() => onSelect(version, index)}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-2 text-left text-[13px] outline-none transition-colors hover:bg-accent/60 focus-visible:bg-accent",
                shown && "bg-accent",
              )}
            >
              <span
                className={cn(
                  "flex h-6 min-w-9 items-center justify-center rounded-md border border-border/70 font-mono text-[11px] tabular-nums",
                  shown && "border-foreground/30 bg-background",
                )}
              >
                v{version.version ?? "?"}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate">{version.author ?? "Unknown author"}</span>
                <span className="block text-xs text-muted-foreground" title={formatDateTime(version.modified)}>
                  {formatDateTime(version.modified)}
                  {version.size !== undefined ? ` · ${formatBytes(version.size)}` : ""}
                </span>
              </span>
              {index === 0 ? (
                <span className="rounded-sm bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                  Latest
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
